import { FetchArgs, createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery, internalHeaders } from './baseQuery';
import {
  AllocationStatusResponse,
  CarAllocationResponseInCarOrder,
  CarAllocationResponseInDriverOrder,
  GetCarAllocationCondition,
  GetUnallocatedResponse,
  SearchUnallocatedByWasteItemConditions,
  SearchUnallocatedByWasteItemResponse,
} from '../types/carAllocation';
import { extinctNullFromParams, extinctFalsyFromParams } from './nullExtict';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';
import { AllocationTask } from '../pages/allocations/components/DayAllocations/allocationTableUtility';
import { addDays, format } from 'date-fns';
import { Middleware } from '@reduxjs/toolkit';
import { contractApi } from './contractApi';

type AllocationReqObjBase = {
  workStartAt: string;
  workCategoryId: number;
  userId?: number;
  carId?: number;
  isLocked: boolean;
};
type AllocatedReqObj = {
  carAllocationId: number;
} & AllocationReqObjBase;
type UnallocatedReqObj = {
  contractDetailId: number;
} & AllocationReqObjBase;

// 配車編集 API の日付フォーマットは RFC3339
const RFC3339Format = "yyyy-MM-dd'T'HH:mm:ssXXX";
const generateEditAllocationRequest = (reqs: AllocationTask[]) => {
  return reqs.map<AllocatedReqObj | UnallocatedReqObj>((req) =>
    req.type === 'unallocated'
      ? {
          contractDetailId: req.contractDetailId,

          workStartAt: format(new Date(req.startDate), RFC3339Format),
          workCategoryId: req.workCategory.id,
          userId: req.userId,
          carId: req.carId,
          isLocked: req.isLocked,
        }
      : {
          carAllocationId: req.carAllocationId,
          contractDetailId: req.contractDetailId,

          workStartAt: format(new Date(req.startDate), RFC3339Format),
          workCategoryId: req.workCategory.id,
          userId: req.userId,
          carId: req.carId,
          isLocked: req.isLocked,
        }
  );
};
export const carAllocationsApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'carAllocations',
  tagTypes: ['CarAllocations', ...errorTagTypes],
  endpoints: (builder) => {
    const getCalcAllocationStatusQueryFn = (): Omit<FetchArgs, 'headers'> => ({
      url: `/car-allocations/calc-allocation`,
    });
    const getCalcAllocationStatusQueryOptions: Parameters<
      typeof builder.query<AllocationStatusResponse, void>
    >[0] = {
      query: getCalcAllocationStatusQueryFn,
      providesTags: (_res, error) => [
        { type: 'CarAllocations', id: 'Calc' },
        ...handleErrorTags(error),
      ],
    };

    return {
      getAllocationsInDriverOrder: builder.query<
        CarAllocationResponseInDriverOrder,
        GetCarAllocationCondition
      >({
        query: (condition) => ({
          url: `/car-allocations`,
          params: {
            ...extinctFalsyFromParams(condition),
            carAllocationsGroupBy: 'driver',
          },
        }),
        providesTags: (_res, error) => [
          { type: 'CarAllocations', id: 'Driver' },
          ...handleErrorTags(error),
        ],
      }),
      getAllocationsInCarOrder: builder.query<
        CarAllocationResponseInCarOrder,
        GetCarAllocationCondition
      >({
        query: (condition) => ({
          url: `/car-allocations`,
          params: {
            ...extinctFalsyFromParams(condition),
            carAllocationsGroupBy: 'car',
          },
        }),
        providesTags: (_res, error) => [
          { type: 'CarAllocations', id: 'Car' },
          ...handleErrorTags(error),
        ],
      }),
      postAllocation: builder.mutation<void, AllocationTask[]>({
        query: (req) => ({
          url: `/car-allocations`,
          method: 'POST',
          body: {
            carAllocations: generateEditAllocationRequest(req),
          },
        }),
        invalidatesTags: (_res, error) => [
          { type: 'CarAllocations', id: 'Calc' },
          { type: 'CarAllocations', id: 'Car' },
          { type: 'CarAllocations', id: 'Driver' },
          { type: 'CarAllocations', id: 'Unallocated' },
          ...handleErrorTags(error),
        ],
      }),
      getUnallocated: builder.query<
        GetUnallocatedResponse,
        GetCarAllocationCondition
      >({
        query: (conditions) => ({
          url: `/car-allocations/unallocated`,
          params: extinctNullFromParams(conditions),
        }),
        providesTags: (_res, error) => [
          { type: 'CarAllocations', id: 'Unallocated' },
          ...handleErrorTags(error),
        ],
      }),
      getCalcAllocationStatus: builder.query(
        getCalcAllocationStatusQueryOptions
      ),
      getCalcAllocationStatusWithoutLoading: builder.query({
        ...getCalcAllocationStatusQueryOptions,
        query: () => ({
          ...getCalcAllocationStatusQueryFn(),
          headers: internalHeaders({ loading: 'suppress' }),
        }),
      }),
      searchUnallocatedByWasteItem: builder.query<
        SearchUnallocatedByWasteItemResponse,
        SearchUnallocatedByWasteItemConditions
      >({
        query: (conditions) => ({
          url: `/car-allocations/unallocated/waste-items`,
          params: extinctFalsyFromParams({
            ...conditions,
            endDate: format(
              addDays(new Date(conditions.startDate), 6),
              'yyyy-MM-dd'
            ),
          }),
        }),
        providesTags: (_res, error) => [
          { type: 'CarAllocations', id: 'Unallocated' },
          ...handleErrorTags(error),
        ],
      }),
      orderCalcAllocation: builder.mutation<
        { optimizationJobId: number },
        void
      >({
        query: () => ({
          url: `/car-allocations/calc-allocation`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'CarAllocations', id: 'Calc' }],
      }),
      deleteCalcAllocationFailed: builder.mutation<void, number>({
        query: (id) => ({
          url: `/car-allocations/calc-allocation/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'CarAllocations', id: 'Calc' }],
      }),
      confirmCalcAllocationFailed: builder.mutation<void, number>({
        query: (id) => ({
          url: `/car-allocations/calc-allocation/${id}/confirm`,
          method: 'POST',
        }),
        invalidatesTags: [
          { type: 'CarAllocations', id: 'Calc' },
          { type: 'CarAllocations', id: 'Car' },
          { type: 'CarAllocations', id: 'Driver' },
          { type: 'CarAllocations', id: 'Unallocated' },
        ],
      }),
      cancelCalcAllocation: builder.mutation<void, number>({
        query: (id) => ({
          url: `/car-allocations/calc-allocation/${id}/abort`,
          method: 'POST',
        }),
        invalidatesTags: [{ type: 'CarAllocations', id: 'Calc' }],
      }),
      deleteCarAllocation: builder.mutation<void, number>({
        query: (id) => ({
          url: `/car-allocations/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: [
          { type: 'CarAllocations', id: 'Calc' },
          { type: 'CarAllocations', id: 'Car' },
          { type: 'CarAllocations', id: 'Driver' },
          { type: 'CarAllocations', id: 'Unallocated' },
        ],
      }),
      toggleCarAllocationLock: builder.mutation<
        void,
        { carAllocationId: number; isLocked: boolean }
      >({
        query: ({ carAllocationId, ...body }) => ({
          url: `/car-allocations/${carAllocationId}/lock`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: [
          { type: 'CarAllocations', id: 'Car' },
          { type: 'CarAllocations', id: 'Driver' },
        ],
      }),
    };
  },
});

/**
 * 配車タスク削除後に契約一覧取得 API のキャッシュが破棄されるよう保証するための Middleware。
 *
 * `carAllocationApi` のエンドポイントに記載した `invalidatesTags` は
 * `carAllocationsApi` 内のエンドポイントしかキャッシュを破棄しないため、
 * 別の API 定義内のエンドポイントのキャッシュ無効化には別途外部からキャッシュタグを無効化する必要がある。
 *
 * API アクセスすれば確実に呼ばれるようにするため Middleware として記述する。
 */
export const afterDeleteCarAllocationMiddleware: Middleware =
  (store) => (next) => (action) => {
    if (
      carAllocationsApi.endpoints.deleteCarAllocation.matchFulfilled(action) ||
      carAllocationsApi.endpoints.confirmCalcAllocationFailed.matchFulfilled(
        action
      ) ||
      carAllocationsApi.endpoints.postAllocation.matchFulfilled(action)
    ) {
      store.dispatch(
        contractApi.util.invalidateTags([{ type: 'Contract', id: 'List' }])
      );
    }
    return next(action);
  };

export const {
  useGetAllocationsInDriverOrderQuery,
  useGetAllocationsInCarOrderQuery,
  useGetUnallocatedQuery,
  usePostAllocationMutation,
  useSearchUnallocatedByWasteItemQuery,
  useGetCalcAllocationStatusQuery,
  useGetCalcAllocationStatusWithoutLoadingQuery,
  useOrderCalcAllocationMutation,
  useDeleteCalcAllocationFailedMutation,
  useConfirmCalcAllocationFailedMutation,
  useCancelCalcAllocationMutation,
  useDeleteCarAllocationMutation,
  useToggleCarAllocationLockMutation,
} = carAllocationsApi;
